import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bike, ShoppingBasket, Smartphone, Truck, Minus, Plus, Trash2, ShoppingCart } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useSite } from "../context/SiteContext";
import { iconFor } from "../lib/icons";
import { SmartLink } from "../pages/Home";
import { api, fmt } from "../lib/api";
import { SoonBadge, btnPrimary } from "./ui";

const SERVICES = [
  { label: "Pedir transporte OBÔGO", icon: Bike, to: "/obogo", soon: true, testid: "qs-obogo" },
  { label: "Comprar produtos BÔ Market", icon: ShoppingBasket, to: "/catalogo", testid: "qs-market" },
  { label: "Recarga CST", icon: Smartphone, to: "/recargas", testid: "qs-cst" },
  { label: "Recarga Unitel", icon: Smartphone, to: "/recargas", testid: "qs-unitel" },
  { label: "Entregas OBÔGO", icon: Truck, to: "/obogo", testid: "qs-delivery" },
];

export function QuickServices() {
  const { site } = useSite();
  const [rideDemo, setRideDemo] = useState(false);
  useEffect(() => { api.get("/ride-demo/status").then(({ data }) => setRideDemo(data.enabled)).catch(() => {}); }, []);
  const base = site ? site.quick_services.filter((q) => q.active).map((q) => ({ label: q.label, icon: iconFor(q.icon), to: q.link, soon: q.soon, testid: q.id })) : SERVICES;
  // While the ride DEMO is on, the "coming soon" transport shortcut points to the clearly labelled demo.
  const services = base.map((q) => (rideDemo && q.soon && q.to === "/obogo" ? { ...q, to: "/mobilidade", soon: false, demo: true } : q));
  if (!services.length) return null;
  return (
    <div className="bo-card p-4" data-testid="quick-services">
      <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-500">Serviços rápidos</h3>
      <div className="flex flex-col gap-2">
        {services.map(({ label, icon: Icon, to, soon, demo, testid }) => (
          <SmartLink key={testid} to={to} data-testid={testid}
            className="group flex items-center gap-3 rounded-xl border border-gray-100 p-2.5 transition-colors hover:border-bo-green/40 hover:bg-bo-green/5">
            <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${soon ? "bg-gray-100 text-gray-500" : "bg-bo-green text-bo-yellow"}`}>
              <Icon className="h-[18px] w-[18px]" />
            </span>
            <span className="flex-1 text-sm font-medium leading-tight">{label}</span>
            {soon && <SoonBadge />}
            {demo && <span className="rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-extrabold uppercase text-bo-ink" data-testid={`${testid}-demo-badge`}>DEMO</span>}
          </SmartLink>
        ))}
      </div>
    </div>
  );
}

export function MiniCart() {
  const { items, setQty, remove, quote } = useCart();
  return (
    <div className="bo-card p-4" data-testid="mini-cart">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-gray-500">
        <ShoppingCart className="h-4 w-4" /> Carrinho
      </h3>
      {!items.length && <p className="py-6 text-center text-sm text-gray-400" data-testid="mini-cart-empty">O seu carrinho está vazio</p>}
      <div className="bo-scroll flex max-h-[300px] flex-col gap-3 overflow-y-auto">
        {items.map((i) => (
          <div key={i.offer_id} className="flex gap-3" data-testid={`mini-cart-item-${i.offer_id}`}>
            <img src={i.image} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{i.name}</p>
              <p className="truncate text-[11px] text-gray-500">{i.store_name}</p>
              <div className="mt-1 flex items-center justify-between">
                <div className="flex items-center gap-1 rounded-full border border-gray-200">
                  <button onClick={() => setQty(i.offer_id, i.qty - 1)} className="p-1" data-testid={`mini-cart-dec-${i.offer_id}`}><Minus className="h-3 w-3" /></button>
                  <span className="w-5 text-center text-xs font-semibold" data-testid={`mini-cart-qty-${i.offer_id}`}>{i.qty}</span>
                  <button onClick={() => setQty(i.offer_id, i.qty + 1)} className="p-1" data-testid={`mini-cart-inc-${i.offer_id}`}><Plus className="h-3 w-3" /></button>
                </div>
                <button onClick={() => remove(i.offer_id)} className="text-gray-400 hover:text-red-500" data-testid={`mini-cart-remove-${i.offer_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {items.length > 0 && <CartTotals quote={quote} />}
      {items.length > 0 && <Link to="/checkout" className={`${btnPrimary} mt-4 w-full`} data-testid="mini-cart-checkout-btn">Finalizar compra</Link>}
    </div>
  );
}

export function CartTotals({ quote }) {
  const row = (label, value, testid, strong) => (
    <div className={`flex justify-between ${strong ? "text-base font-bold text-bo-ink" : "text-sm text-gray-600"}`}>
      <span>{label}</span><span data-testid={testid}>{value}</span>
    </div>
  );
  return (
    <div className="mt-4 space-y-1.5 border-t border-gray-100 pt-3">
      {row("Subtotal", fmt(quote?.subtotal), "cart-subtotal")}
      {row("Entrega", fmt(quote?.delivery_fee), "cart-delivery")}
      {row("Total", fmt(quote?.total), "cart-total", true)}
      {quote?.problems?.map((p) => <p key={p.offer_id} className="text-xs text-red-600" data-testid="cart-problem">{p.reason}</p>)}
    </div>
  );
}

export default function RightRail() {
  return (
    <aside className="hidden flex-col gap-4 xl:flex" data-testid="right-rail">
      <div className="sticky top-[88px] flex flex-col gap-4">
        <QuickServices />
        <MiniCart />
      </div>
    </aside>
  );
}
